import { HHStack } from '@hinge-health/react-component-library';
import { DataGridPro, GridRowId, GridSortModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useBillingWriteAccess } from '../../../../hooks/use-billing-write-access';
import { useFilterUrlSync } from '../../../../hooks/use-filter-url-sync';
import { FooterAction, GridFooter } from '../../../grid-components/grid-footer';
import { GridLevelName } from '../../../grid-components/grid-types';
import { NoRowsOverlay } from '../../../grid-components/no-rows-or-error-overlay';
import { useGridPagination } from '../../../grid-components/use-grid-pagination';
import { useGridSelection } from '../../../grid-components/use-grid-selection';
import {
  BillManagementGridFilterModelType,
  BillTypeData,
  BillTypeDataList,
} from '../../bill-grid-components/bill-management-types';
import { useGridActions } from '../../bill-grid-components/use-grid-actions';
import {
  doesClientInsurerChangeMatch,
  generateEnrichedData,
  getGridCount,
  hasAllowedEligibilityCheckHolds,
  hasReleasableHoldTypes,
} from '../../utils';
import BillManagementFilters from '../filters-toolbar';
import { useBillColumns } from './use-bill-columns';
import { useBillQueries } from './use-bill-queries';

export const default90DayLookbackStart = dayjs().subtract(90, 'days').toDate();
export const defaultBillManagementGridFilterModel: BillManagementGridFilterModelType =
  {
    userIds: [],
    source: null,
    sourceIds: [],
    startDate: null,
    endDate: null,
    clientIds: [],
    insurerIds: [],
    partnershipIds: [],
    held: false,
    submitted: false,
    holdReasons: [],
    billingTypes: [],
    submissionStatusTypes: [],
    paymentTypes: [],
    billIds: [],
    sourceStartDate: null,
    sourceEndDate: null,
  };

export const BILL_MANAGEMENT_SEARCH_PROMPT =
  'Choose filters and select Apply to load bills.';

const hasNarrowingFilter = (
  filters: BillManagementGridFilterModelType,
): boolean =>
  filters.clientIds.length > 0 ||
  filters.insurerIds.length > 0 ||
  filters.partnershipIds.length > 0 ||
  filters.userIds.length > 0 ||
  filters.billIds.length > 0 ||
  filters.sourceIds.length > 0;

const noneAreSubmissions = (bills: BillTypeDataList): boolean =>
  bills.every(bill => bill.submissionId == null);

const BillManagementGrid = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const [filterParams, setFilterParamsState] = useFilterUrlSync(
    defaultBillManagementGridFilterModel,
  );

  const [initiallyScoped] = useState(() => hasNarrowingFilter(filterParams));
  const [searchRequested, setSearchRequested] = useState(initiallyScoped);

  const setFilterParams: typeof setFilterParamsState = useCallback(
    value => {
      setSearchRequested(true);
      setFilterParamsState(value);
    },
    [setFilterParamsState],
  );

  const resetSearch = useCallback(() => {
    setSearchRequested(false);
    setFilterParamsState(defaultBillManagementGridFilterModel);
  }, [setFilterParamsState]);

  const [gridRows, setGridRows] = useState<BillTypeDataList>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const getBillRowId = useCallback(
    (row: BillTypeData): GridRowId => row.billId,
    [],
  );

  const {
    handleRowSelectionChange,
    clearAllSelections,
    selectedRows,
    visibleSelectedIds,
  } = useGridSelection(gridRows, getBillRowId, filterParams);

  const {
    paginationModel,
    setPaginationModel,
    handlePaginationChange,
    handlePageOptionChange,
  } = useGridPagination({ page: 0, pageSize: 100 });

  const {
    ciData,
    contractsData,
    clientsData,
    insurersData,
    partnershipsData,
    billsData,
    billingTypesData,
    isLoading,
    hasError,
    searchClientInsurerIds,
  } = useBillQueries(filterParams, paginationModel, sortModel, searchRequested);

  // Actions:
  const gridActions = useGridActions({
    clientsData,
    insurersData,
    clearAllSelections,
    gridLevel: GridLevelName.BILL,
  });

  const moreDetails = useCallback(
    (id: GridRowId) => () => {
      const row = gridRows.find(item => item.billId === id);
      if (row) {
        gridActions.moreDetails(row.billId);
      }
    },
    [gridRows, gridActions],
  );

  const archiveBill = useCallback(
    (id?: GridRowId) => () => {
      const rows =
        typeof id == 'number'
          ? (gridRows.filter(data => data.billId === id) ?? [])
          : selectedRows;
      gridActions.archiveBill(rows);
    },
    [gridRows, selectedRows, gridActions],
  );

  const releaseBill = useCallback(
    (id?: GridRowId) => () => {
      const rows =
        typeof id == 'number'
          ? (gridRows.filter(data => data.billId === id) ?? [])
          : selectedRows;
      gridActions.releaseBill(rows);
    },
    [gridRows, selectedRows, gridActions],
  );

  const checkEligibility = useCallback(
    (id?: GridRowId) => () => {
      const rows =
        typeof id == 'number'
          ? (gridRows.filter(data => data.billId === id) ?? [])
          : selectedRows;
      gridActions.checkEligibility(rows);
    },
    [gridRows, selectedRows, gridActions],
  );

  const checkEligibilityStatus = useCallback(
    () => () => {
      gridActions.checkEligibilityStatus();
    },
    [gridActions],
  );

  const createManualHold = useCallback(
    (id?: GridRowId) => () => {
      const rows =
        typeof id == 'number'
          ? (gridRows.filter(data => data.billId === id) ?? [])
          : selectedRows;
      gridActions.createManualHold(rows);
    },
    [gridRows, selectedRows, gridActions],
  );

  const processCopayment = useCallback(
    (id?: GridRowId) => () => {
      const rows =
        typeof id == 'number'
          ? (gridRows.filter(data => data.billId === id) ?? [])
          : selectedRows;
      gridActions.processCopayment(rows);
    },
    [gridRows, selectedRows, gridActions],
  );

  const billManagementColumns = useBillColumns({
    clientsData,
    insurersData,
    moreDetails,
    releaseBill,
    archiveBill,
    submitted: filterParams.submitted,
    canWrite,
  });

  useEffect(() => {
    if (billsData && !hasError) {
      const rows = generateEnrichedData(
        billsData.items,
        ciData,
        partnershipsData,
        contractsData.items,
      );
      const ciIdsFromContracts = new Set(
        contractsData.items.map(c => c.clientsInsurerId),
      );
      if (ciIdsFromContracts.size > 0) {
        searchClientInsurerIds(ciIdsFromContracts);
      }
      setGridRows(rows);
    }
  }, [
    billsData,
    ciData,
    partnershipsData,
    contractsData,
    hasError,
    searchClientInsurerIds,
  ]);

  const hasSelectedRows = selectedRows.length > 0;

  const overlayProps = {
    error: hasError,
    ...(searchRequested ? {} : { message: BILL_MANAGEMENT_SEARCH_PROMPT }),
  } as Record<string, unknown>;

  const getBulkActions = (): FooterAction[] => {
    // Read-only action: opens a modal of locally-stored eligibility-check
    // statuses. Available to all roles; everything else here mutates.
    const eligibilityStatusAction: FooterAction = {
      action: checkEligibilityStatus(),
      actionLabel: 'Status of Eligibility Checks',
      disabled: false,
    };

    if (!canWrite) {
      return [eligibilityStatusAction];
    }

    return [
      {
        action: archiveBill(),
        actionLabel: 'Archive Bills',
        disabled: !hasSelectedRows || filterParams.submitted === true,
      },
      {
        action: releaseBill(),
        actionLabel: 'Release Holds',
        disabled:
          !hasSelectedRows ||
          filterParams.held === false ||
          !hasReleasableHoldTypes(selectedRows) ||
          !doesClientInsurerChangeMatch(selectedRows),
      },
      {
        action: checkEligibility(),
        actionLabel: 'Check Eligibility',
        disabled:
          !hasSelectedRows ||
          filterParams.submitted === true ||
          filterParams.held === false ||
          !hasAllowedEligibilityCheckHolds(selectedRows),
      },
      eligibilityStatusAction,
      {
        action: createManualHold(),
        actionLabel: 'Create Manual Hold',
        disabled: !hasSelectedRows || !noneAreSubmissions(selectedRows),
      },
      {
        action: processCopayment(),
        actionLabel: 'Process Copayment',
        disabled:
          !hasSelectedRows ||
          !selectedRows.some(
            row => row.submissionId != null && row.submissionStatus !== 'void',
          ),
      },
    ];
  };

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={6}
      justifyContent="space-between"
      minHeight="0"
    >
      <BillManagementFilters
        clients={clientsData}
        insurers={insurersData}
        partnerships={partnershipsData}
        billingTypes={billingTypesData}
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        setPaginationModel={setPaginationModel}
        isLoading={isLoading}
        hasError={hasError}
        onResetSearch={resetSearch}
        defaultFiltersOpen={!initiallyScoped}
      />

      <HHStack direction="column" flex={1} minHeight="0">
        <DataGridPro
          columns={billManagementColumns}
          rows={gridRows.map(row => ({ id: row.billId, ...row }))}
          loading={isLoading}
          //  pagination
          rowCount={getGridCount(gridRows.length, paginationModel)}
          disableRowSelectionOnClick
          pagination
          paginationModel={paginationModel}
          paginationMode="server"
          // filtering
          filterMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newSortModel): void => setSortModel(newSortModel)}
          disableMultipleRowSelection={false}
          checkboxSelection
          rowSelectionModel={visibleSelectedIds}
          onRowSelectionModelChange={handleRowSelectionChange}
          pinnedColumns={{ left: ['__check__'], right: ['actions'] }}
          hideFooter
          // Force all columns to render in test environment only
          {...(process.env.NODE_ENV === 'test' && {
            disableVirtualization: true,
          })}
          slots={{
            noResultsOverlay: NoRowsOverlay,
            noRowsOverlay: NoRowsOverlay,
          }}
          slotProps={{
            noResultsOverlay: overlayProps,
            noRowsOverlay: overlayProps,
          }}
        />

        <GridFooter
          // pagination
          page={paginationModel.page}
          rowsPerPage={paginationModel.pageSize}
          count={getGridCount(gridRows.length, paginationModel)}
          onPageChange={handlePaginationChange}
          onRowsPerPageChange={handlePageOptionChange}
          // selection
          selectedRows={selectedRows}
          actions={getBulkActions()}
          gridLevel={GridLevelName.BILL}
          getAmount={(row): number | null => row.billAmount ?? null}
        />
      </HHStack>
    </HHStack>
  );
};

export default BillManagementGrid;
